import React, { useState, useEffect } from 'react';
import { 
  CheckSquare, Plus, Trash2, Edit2, Save, X, Target, Flag,
  Calendar, Clock, AlertTriangle, CheckCircle, Circle, ChevronDown, ChevronRight,
  Bookmark, Star, RefreshCw, Filter
} from 'lucide-react';
import { toast } from 'sonner';
import getApiUrl from '../utils/getApiUrl';

const API = getApiUrl();

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


// ═══════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL - GESTION PROJET
// ═══════════════════════════════════════════════════════════════
const ProjectManagement = () => {
  const [activeTab, setActiveTab] = useState('pensebete');
  const [penseBete, setPenseBete] = useState([]);
  const [tachesDev, setTachesDev] = useState([]);
  const [objectifs, setObjectifs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newTask, setNewTask] = useState('');
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API}/api/project-management/all`);
      if (response.ok) {
        const data = await response.json();
        setPenseBete(data.pense_bete || []);
        setTachesDev(data.taches_dev || []);
        setObjectifs(data.objectifs || getDefaultObjectifs());
      } else {
        // Utiliser les données par défaut
        setObjectifs(getDefaultObjectifs());
        setTachesDev(getDefaultTaches());
      }
    } catch (error) {
      console.error('Erreur chargement:', error);
      setObjectifs(getDefaultObjectifs());
      setTachesDev(getDefaultTaches());
    }
    setLoading(false);
  };

  const getDefaultObjectifs = () => [
    {
      id: 1,
      priority: 1,
      title: "Finir Modal Client",
      description: "Compléter toutes les fonctionnalités du modal client configurable",
      status: 'in-progress',
      subtasks: [
        { id: 1, title: "Preview avec client réel", done: true },
        { id: 2, title: "Onglets dynamiques fonctionnels", done: true },
        { id: 3, title: "Chargement données RDV/Appels", done: true },
        { id: 4, title: "Tests avec l'équipe", done: false },
      ]
    },
    {
      id: 2,
      priority: 2,
      title: "Intégration Google Drive",
      description: "Stockage des factures dans Google Drive",
      status: 'pending',
      subtasks: []
    },
    {
      id: 3,
      priority: 3,
      title: "Rachel IA - Recherche factures",
      description: "Recherche de contenu dans les factures via IA",
      status: 'pending',
      subtasks: []
    }
  ];

  const getDefaultTaches = () => [
    { id: 1, title: "Implémenter stockage Google Drive", priority: 'high', status: 'pending', dashboard: 'Comptabilité' },
    { id: 2, title: "Refactorer server.py en modules", priority: 'medium', status: 'backlog', dashboard: 'Transversal' },
    { id: 3, title: "Collection clients_pipeline supprimée", priority: 'low', status: 'done', dashboard: 'Transversal' },
  ];

  // Pense-bête: Ajouter une tâche
  const addPenseBete = async () => {
    if (!newTask.trim()) return;
    
    const task = {
      id: Date.now(),
      text: newTask,
      done: false,
      createdAt: new Date().toISOString()
    };
    
    const updated = [...penseBete, task];
    setPenseBete(updated);
    setNewTask('');
    
    // Sauvegarder
    try {
      await authFetch(`${API}/api/project-management/pense-bete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tasks: updated })
      });
      toast.success('Tâche ajoutée');
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
    }
  };

  // Pense-bête: Toggle done
  const togglePenseBete = async (id) => {
    const updated = penseBete.map(t => 
      t.id === id ? { ...t, done: !t.done } : t
    );
    setPenseBete(updated);
    
    try {
      await authFetch(`${API}/api/project-management/pense-bete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tasks: updated })
      });
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
    }
  };

  // Pense-bête: Supprimer
  const deletePenseBete = async (id) => {
    const updated = penseBete.filter(t => t.id !== id);
    setPenseBete(updated);
    
    try {
      await authFetch(`${API}/api/project-management/pense-bete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tasks: updated })
      });
      toast.success('Tâche supprimée');
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
    }
  };

  const tabs = [
    { id: 'pensebete', label: 'Mon Pense-Bête', icon: Bookmark },
    { id: 'taches', label: 'Tâches Développement', icon: CheckSquare },
    { id: 'objectifs', label: 'Objectifs & Priorités', icon: Target },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600/20 to-teal-600/20 border border-emerald-500/30 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-2">
          <CheckSquare className="w-8 h-8 text-emerald-400" />
          <h2 className="text-2xl font-bold text-white">Gestion Projet</h2>
        </div>
        <p className="text-slate-400">
          Vos tâches personnelles, les développements en cours et les objectifs de déploiement
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              activeTab === tab.id
                ? 'bg-emerald-500 text-white'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Contenu */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-emerald-400" />
          </div>
        ) : (
          <>
            {/* Tab: Pense-Bête */}
            {activeTab === 'pensebete' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Bookmark className="w-5 h-5 text-amber-400" />
                  Mon Pense-Bête
                </h3>
                <p className="text-slate-400 text-sm">
                  Notez vos idées et tâches pour ne rien oublier
                </p>

                {/* Ajouter une tâche */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newTask}
                    onChange={(e) => setNewTask(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addPenseBete()}
                    placeholder="Ajouter une tâche..."
                    className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:border-emerald-500 focus:outline-none"
                  />
                  <button
                    onClick={addPenseBete}
                    className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors flex items-center gap-2"
                  >
                    <Plus size={16} />
                    Ajouter
                  </button>
                </div>

                {/* Liste des tâches */}
                <div className="space-y-2">
                  {penseBete.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Bookmark className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Votre pense-bête est vide</p>
                      <p className="text-sm mt-1">Ajoutez des tâches pour ne rien oublier</p>
                    </div>
                  ) : (
                    penseBete.map(task => (
                      <div 
                        key={task.id} 
                        className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                          task.done ? 'bg-slate-900/30' : 'bg-slate-900/50'
                        }`}
                      >
                        <button
                          onClick={() => togglePenseBete(task.id)}
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                            task.done 
                              ? 'bg-emerald-500 border-emerald-500' 
                              : 'border-slate-500 hover:border-emerald-500'
                          }`}
                        >
                          {task.done && <CheckCircle size={14} className="text-white" />}
                        </button>
                        <span className={`flex-1 ${task.done ? 'text-slate-500 line-through' : 'text-white'}`}>
                          {task.text}
                        </span>
                        <button
                          onClick={() => deletePenseBete(task.id)}
                          className="p-1 text-slate-500 hover:text-red-400 transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Tab: Tâches Dev */}
            {activeTab === 'taches' && (
              <TachesDevTab taches={tachesDev} />
            )}

            {/* Tab: Objectifs */}
            {activeTab === 'objectifs' && (
              <ObjectifsTab objectifs={objectifs} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// TAB: TÂCHES DÉVELOPPEMENT
// ═══════════════════════════════════════════════════════════════
const TachesDevTab = ({ taches }) => {
  const priorityColors = {
    high: 'bg-red-500/20 text-red-400 border-red-500/30',
    medium: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    low: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
  };

  const priorityLabels = {
    high: '🔴 Haute',
    medium: '🟡 Moyenne',
    low: '🟢 Basse'
  };

  const statusLabels = {
    pending: 'En attente',
    'in-progress': 'En cours',
    backlog: 'Backlog',
    done: 'Terminé'
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-white flex items-center gap-2">
        <CheckSquare className="w-5 h-5 text-blue-400" />
        Tâches Développement
      </h3>
      <p className="text-slate-400 text-sm">
        Tâches techniques alimentées automatiquement par l'agent
      </p>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-700">
              <th className="text-left py-3 px-4 text-slate-400 font-medium">Priorité</th>
              <th className="text-left py-3 px-4 text-slate-400 font-medium">Tâche</th>
              <th className="text-left py-3 px-4 text-slate-400 font-medium">Dashboard</th>
              <th className="text-left py-3 px-4 text-slate-400 font-medium">Statut</th>
            </tr>
          </thead>
          <tbody>
            {taches.map(tache => (
              <tr key={tache.id} className="border-b border-slate-700/50 hover:bg-slate-800/30">
                <td className="py-3 px-4">
                  <span className={`text-xs px-2 py-1 rounded border ${priorityColors[tache.priority]}`}>
                    {priorityLabels[tache.priority]}
                  </span>
                </td>
                <td className="py-3 px-4 text-white">{tache.title}</td>
                <td className="py-3 px-4 text-slate-400">{tache.dashboard}</td>
                <td className="py-3 px-4">
                  <span className="text-xs bg-slate-700 text-slate-300 px-2 py-1 rounded">
                    {statusLabels[tache.status]}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// TAB: OBJECTIFS & PRIORITÉS
// ═══════════════════════════════════════════════════════════════
const ObjectifsTab = ({ objectifs }) => {
  const [expandedObjectifs, setExpandedObjectifs] = useState({});

  const toggleObjectif = (id) => {
    setExpandedObjectifs(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const priorityColors = {
    1: 'border-red-500 bg-red-500/10',
    2: 'border-amber-500 bg-amber-500/10',
    3: 'border-emerald-500 bg-emerald-500/10'
  };

  const statusIcons = {
    'in-progress': <Clock className="w-4 h-4 text-amber-400" />,
    'pending': <Circle className="w-4 h-4 text-slate-400" />,
    'done': <CheckCircle className="w-4 h-4 text-emerald-400" />
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Target className="w-5 h-5 text-red-400" />
          Objectifs & Priorités de Déploiement
        </h3>
      </div>
      
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
        <p className="text-amber-400 text-sm flex items-center gap-2">
          <AlertTriangle size={16} />
          <strong>Rappel :</strong> Restez focus sur les objectifs prioritaires avant de partir sur de nouvelles fonctionnalités !
        </p>
      </div>

      <div className="space-y-4">
        {objectifs.map(objectif => (
          <div 
            key={objectif.id} 
            className={`border-l-4 rounded-lg overflow-hidden ${priorityColors[objectif.priority]}`}
          >
            <button
              onClick={() => toggleObjectif(objectif.id)}
              className="w-full flex items-center justify-between p-4 hover:bg-slate-800/30 transition-colors"
            >
              <div className="flex items-center gap-3">
                {statusIcons[objectif.status]}
                <div className="text-left">
                  <div className="flex items-center gap-2">
                    <span className="text-xs bg-slate-700 text-slate-300 px-2 py-0.5 rounded">
                      P{objectif.priority}
                    </span>
                    <span className="font-semibold text-white">{objectif.title}</span>
                  </div>
                  <p className="text-sm text-slate-400 mt-1">{objectif.description}</p>
                </div>
              </div>
              {objectif.subtasks?.length > 0 && (
                expandedObjectifs[objectif.id] ? <ChevronDown size={20} /> : <ChevronRight size={20} />
              )}
            </button>

            {expandedObjectifs[objectif.id] && objectif.subtasks?.length > 0 && (
              <div className="border-t border-slate-700 p-4 bg-slate-900/30">
                <div className="space-y-2">
                  {objectif.subtasks.map(sub => (
                    <div key={sub.id} className="flex items-center gap-3">
                      {sub.done ? (
                        <CheckCircle size={16} className="text-emerald-400" />
                      ) : (
                        <Circle size={16} className="text-slate-500" />
                      )}
                      <span className={sub.done ? 'text-slate-500 line-through' : 'text-slate-300'}>
                        {sub.title}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 text-xs text-slate-500">
                  {objectif.subtasks.filter(s => s.done).length} / {objectif.subtasks.length} terminé(s)
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProjectManagement;
